/**
 * Provider-agnostic authentication configuration.
 *
 * Central configuration for identity provider settings.
 * Consolidates cognito.ts and adds support for multiple providers.
 *
 * Supports:
 * - Single provider mode: 'cognito' or 'entra'
 * - Dual provider mode: 'both' for transition periods
 * - Local/mock mode: For local development
 */

import { buildOidcConfig, getAuthProvider, useMockAuth } from '../auth/providers/oidc';
import type { AuthProviderType, OidcProviderConfig } from '../auth/providers/types';

/**
 * Authentication configuration for the application.
 */
export interface AuthConfig {
  /** Current auth provider */
  provider: AuthProviderType;
  /** OIDC configuration (null for mock/local mode) */
  oidcConfig: OidcProviderConfig | null;
  /** Whether mock auth is enabled */
  isMockAuth: boolean;
  /** API URL for backend requests */
  apiUrl: string;
  /** Whether dual-provider mode is enabled */
  isDualProvider: boolean;
}

/**
 * Get the API URL based on environment.
 *
 * IMPORTANT: No fallback values - missing environment variables will cause clear errors.
 * See README "Using as a Template" section for required configuration.
 */
function getApiUrl(): string {
  // Deployed config
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  const environment = import.meta.env.VITE_ENVIRONMENT || 'local';

  switch (environment) {
    case 'dev': {
      const devApiUrl = import.meta.env.VITE_DEV_API_URL;
      if (!devApiUrl) {
        throw new Error(
          'VITE_DEV_API_URL is required for dev environment. ' +
          'Please add it to your .env file. See README "Using as a Template" section.'
        );
      }
      return devApiUrl;
    }
    case 'prod': {
      const prodApiUrl = import.meta.env.VITE_PROD_API_URL;
      if (!prodApiUrl) {
        throw new Error(
          'VITE_PROD_API_URL is required for prod environment. ' +
          'Please add it to your .env file. See README "Using as a Template" section.'
        );
      }
      return prodApiUrl;
    }
    case 'local':
    default:
      return 'http://localhost:8000';
  }
}

/**
 * Load authentication configuration.
 *
 * @returns AuthConfig for the current environment
 */
export function loadAuthConfig(): AuthConfig {
  const provider = getAuthProvider();
  const oidcConfig = buildOidcConfig();
  const isMockAuth = useMockAuth();
  const apiUrl = getApiUrl();
  const isDualProvider = provider === 'both';

  return {
    provider,
    oidcConfig,
    isMockAuth,
    apiUrl,
    isDualProvider,
  };
}

// Export singleton config
let _config: AuthConfig | null = null;

/**
 * Get the authentication configuration (singleton).
 */
export function getAuthConfig(): AuthConfig {
  if (_config === null) {
    _config = loadAuthConfig();
  }
  return _config;
}

/**
 * Get oidc-client-ts configuration for AuthProvider.
 *
 * This returns the configuration object expected by react-oidc-context.
 */
export function getOidcClientConfig() {
  const config = getAuthConfig();

  if (!config.oidcConfig) {
    return null;
  }

  return {
    authority: config.oidcConfig.authority,
    client_id: config.oidcConfig.clientId,
    redirect_uri: config.oidcConfig.redirectUri,
    post_logout_redirect_uri: config.oidcConfig.postLogoutRedirectUri,
    scope: config.oidcConfig.scope,
    response_type: config.oidcConfig.responseType,
    // PKCE settings
    automaticSilentRenew: true,
    loadUserInfo: true,
  };
}

// Re-export types
export type { AuthProviderType, OidcProviderConfig };
